import React, { useEffect, useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import * as walletService from '../../services/walletService'
import { getAffiliateCommissionHistory } from '../../services/affiliateService'
import type { AffiliateOutletContext } from './ModernAffiliateLayout'
import type { AffiliateCommissionHistoryItem } from '../../services/affiliateService'
import {
  formatCommissionHistoryDateTime,
  formatCommissionMemberLabel,
} from '../../utils/affiliateHistoryUtils'
import { affiliateCardShell, affiliateMutedText } from './affiliateUi'
import './affiliateLayout.css'

function maskMember(raw: string): string {
  const s = String(raw || '').trim()
  if (!s) return 'สมาชิก'
  if (s.length < 4) return s
  if (/^\d+$/.test(s)) {
    if (s.length >= 10) return s.slice(0, 3) + 'xxxxxx' + s.slice(-1)
    return s.slice(0, 2) + 'xxxxx' + s.slice(-1)
  }
  if (s.length <= 6) return s.slice(0, 1) + 'xxx' + s.slice(-1)
  return s.slice(0, 2) + '****' + s.slice(-3)
}

const AffiliateIncomePage: React.FC = () => {
  useOutletContext<AffiliateOutletContext>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [commissionHistory, setCommissionHistory] = useState<AffiliateCommissionHistoryItem[]>([])

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError(null)
      try {
        const history = await getAffiliateCommissionHistory(100)
        if (!cancelled) {
          setCommissionHistory(history)
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'โหลดข้อมูลไม่สำเร็จ')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  const totalCommission = commissionHistory.reduce(
    (sum, item) => sum + Number(item.commission ?? 0),
    0,
  )

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border text-primary" role="status" />
        <p className="mt-2 c_tertiary">กำลังโหลดข้อมูล...</p>
      </div>
    )
  }
  if (error) {
    return (
      <div className="text-center py-4">
        <p className="c_primary mb-0">{error}</p>
      </div>
    )
  }

  return (
    <div className="row mx__1">
      <div className="col-12 px-1 mb-3">
        <div className="affiliate-stat-card">
          <div className="affiliate-stat-card__label">คอมมิชชั่นรวมจากประวัติ (บาท)</div>
          <div className="affiliate-stat-card__value affiliate-stat-card__value--accent">
            {walletService.formatCurrency(totalCommission)}
          </div>
        </div>
      </div>

      {commissionHistory.length > 0 ? (
        <div className="col-12 px-1 mb-3">
          <div className="affiliate-panel p-3 p-md-4 overflow-hidden" style={affiliateCardShell}>
            <div className="d-flex align-items-center gap-2 mb-3">
              <div
                className="d-flex align-items-center justify-content-center flex-shrink-0"
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  background: 'rgba(49, 208, 126, 0.16)',
                }}
              >
                <i className="fas fa-chart-line" style={{ color: '#4ade80' }} />
              </div>
              <div>
                <h5 className="c_tertiary mb-0">ประวัติคอมมิชชั่น</h5>
                <p className="mb-0 small" style={{ color: affiliateMutedText }}>
                  รายการล่าสุด {commissionHistory.length} รายการ
                </p>
              </div>
            </div>
            <div className="affiliate-table-wrap table-responsive w-100">
              <table className="table text-center mb-0">
                <thead>
                  <tr>
                    <th scope="col">วันที่ เวลา</th>
                    <th scope="col">สมาชิก</th>
                    <th scope="col">ยอดเสีย/ยอดแทง</th>
                    <th scope="col">คอมมิชชั่น</th>
                  </tr>
                </thead>
                <tbody>
                  {commissionHistory.map((item, i) => (
                    <tr key={item._id || `${item.createdAt || item.date}-${i}`}>
                      <td>{formatCommissionHistoryDateTime(item)}</td>
                      <td>{formatCommissionMemberLabel(item, maskMember)}</td>
                      <td>{walletService.formatCurrency(item.betAmount ?? 0)}</td>
                      <td style={{ color: '#4ade80', fontWeight: 600 }}>
                        {walletService.formatCurrency(item.commission ?? 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <div className="col-12 px-1">
          <div className="affiliate-empty-state">
            <i className="fas fa-receipt mb-3" style={{ fontSize: '2rem', color: '#64748b' }} />
            <h4>ไม่มีประวัติคอมมิชชั่น</h4>
          </div>
        </div>
      )}
    </div>
  )
}

export default AffiliateIncomePage
